import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiAlertCircle, FiBell, FiCalendar, FiChevronDown, FiTruck, FiTool, FiDollarSign } from "react-icons/fi";
import fetchData from "../custom_functions/fetchData";
import patchData from "../custom_functions/patchData";
import postData from "../custom_functions/postData";
import { url } from "../utils/config";
import { title } from "../utils/style";

const PAGE_SIZE = 5;

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

function formatCurrency(value) {
  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
}

function formatReminderType(type) {
  if (!type) return "-";
  return type.replace("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(dateInput) {
  if (!dateInput) return "-";
  try {
    const date = new Date(dateInput);
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch (error) {
    return dateInput;
  }
}

function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [acknowledgingId, setAcknowledgingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchReminders();
  }, []);

  function fetchReminders() {
    setIsLoading(true);
    fetchData(
      `${url}/reminders`,
      (error) => {
        setIsLoading(false);
        toast.error(error?.response?.data?.message || "Unable to load reminders", { autoClose: 3000 });
      },
      (response) => {
        const payload = response?.data || response || {};
        const records = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        setReminders(records);
        setCurrentPage(1);
        setExpandedCards({});
        setIsLoading(false);
      },
      false
    );
  }

  const typeBadgeStyles = useMemo(
    () => ({
      service: "bg-emerald-50 border border-emerald-100 text-emerald-700",
      maintenance: "bg-sky-50 border border-sky-100 text-sky-700",
      follow_up: "bg-amber-50 border border-amber-100 text-amber-700",
    }),
    []
  );

  const hasReminders = reminders.length > 0;
  const totalPages = hasReminders ? Math.ceil(reminders.length / PAGE_SIZE) : 0;
  const paginatedReminders = useMemo(() => {
    if (!hasReminders) return [];
    const safePage = Math.min(currentPage, totalPages || 1);
    const startIndex = (safePage - 1) * PAGE_SIZE;
    return reminders.slice(startIndex, startIndex + PAGE_SIZE);
  }, [reminders, currentPage, hasReminders, totalPages]);
  const canGoBack = currentPage > 1;
  const canGoForward = currentPage < totalPages;

  useEffect(() => {
    if (!hasReminders && currentPage !== 1) {
      setCurrentPage(1);
    } else if (hasReminders && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [hasReminders, currentPage, totalPages]);

  useEffect(() => {
    setExpandedCards({});
  }, [currentPage]);

  function toggleCard(reminderId) {
    setExpandedCards((prev) => ({ ...prev, [reminderId]: !prev[reminderId] }));
  }

  function handleAcknowledge(reminder) {
    if (!reminder?.id || reminder?.isAcknowledged) return;
    setAcknowledgingId(reminder.id);
    patchData(
      `${url}/acknowledge_reminder/${reminder.id}`,
      { isAcknowledged: true },
      (error) => {
        setAcknowledgingId(null);
        toast.error(error?.response?.data?.message || "Unable to acknowledge reminder", { autoClose: 3000 });
      },
      () => {
        setAcknowledgingId(null);
        toast.success("Reminder acknowledged", { autoClose: 3000 });
        setReminders((prev) =>
          prev.map((item) =>
            item.id === reminder.id ? { ...item, isAcknowledged: true } : item
          )
        );
      },
      false
    );
  }

  // function handleCreateServiceRequest(reminder) {

  //   postData(`${url}/clone_service_req/${reminder.id}`,
  //     { serviceRequestId: reminder.service_request_id },
  //     (error) => {
  //       toast.error(error?.response?.data?.message || "Unable to create service request", {
  //         autoClose: 3000,
  //       });
  //     },
  //     (data) => {
  //       toast.success(data.message, { autoClose: 3000 });
  //       fetchReminders();
  //     }
  //   );
  // }

  function goToPreviousPage() {
    if (!canGoBack) return;
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }

  function goToNextPage() {
    if (!canGoForward) return;
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  }

  return (
    <div className="p-4" style={{ backgroundColor: "#e6f9ec", borderRadius: "12px" }}>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className={title}>Reminders</div>
        <button
          type="button"
          onClick={fetchReminders}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-200 bg-white px-5 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
        >
          <FiBell size={16} />
          Refresh
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {isLoading && (
          <div className="rounded-2xl border border-emerald-100 bg-white px-6 py-8 text-center text-emerald-600 shadow-[0_18px_40px_rgba(22,101,52,0.12)]">
            Loading reminders...
          </div>
        )}

        {!isLoading && !hasReminders && (
          <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-6 py-16 text-center text-emerald-500 shadow-[0_12px_30px_rgba(22,101,52,0.12)]">
            No reminders found.
          </div>
        )}

        {!isLoading && hasReminders &&
          paginatedReminders.map((reminder) => {
            const reminderId = reminder?.id || `${reminder?.service_request_id}-${reminder?.reminder_date}`;
            const typeKey = reminder?.reminder_type || "service";
            const serviceDetails = reminder?.fc_service_request;
            const isExpanded = Boolean(expandedCards[reminderId]);

            return (
              <div
                key={reminderId}
                className="w-full rounded-2xl border border-emerald-100 bg-white px-5 py-5 shadow-[0_18px_40px_rgba(22,101,52,0.12)]"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-lg text-emerald-700">
                      <FiAlertCircle size={22} />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="text-lg font-semibold text-emerald-900">{formatReminderType(typeKey)} Reminder</div>
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${typeBadgeStyles[typeKey] || "bg-slate-100 border border-slate-200 text-slate-600"
                            }`}
                        >
                          {formatReminderType(typeKey)}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-emerald-500">
                        <div className="flex items-center gap-2">
                          <FiCalendar size={14} />
                          <span>{formatDate(reminder?.reminder_date)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-3 self-start">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleAcknowledge(reminder)}
                        disabled={reminder?.isAcknowledged || acknowledgingId === reminder?.id}
                        className={`rounded-full px-4 py-2 text-xs font-semibold text-white ${reminder?.isAcknowledged
                          ? "bg-emerald-300"
                          : "bg-emerald-600 hover:bg-emerald-700"
                          } disabled:cursor-not-allowed`}
                      >
                        {acknowledgingId === reminder?.id ? "Acknowledging..." : reminder?.isAcknowledged ? "Acknowledged" : "Acknowledge"}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleCard(reminderId)}
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                      >
                        <span>{isExpanded ? "Hide Service Details" : "View Service Details"}</span>
                        <FiChevronDown
                          size={14}
                          className={`transition-transform ${isExpanded ? "rotate-180" : "rotate-0"}`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4">
                    {!serviceDetails ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-slate-500">No service details available</p>
                      </div>
                    ) : (
                      <>
                        {/* Vehicle Info */}
                        {serviceDetails?.fleet_customer_car && (
                          <div className="mb-4 p-3 rounded-xl bg-white border border-emerald-100">
                            <div className="flex items-center gap-2 mb-1">
                              <FiTruck size={14} className="text-emerald-600" />
                              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Vehicle</span>
                            </div>
                            <div className="text-sm font-bold text-emerald-900">
                              {[
                                serviceDetails.fleet_customer_car.make,
                                serviceDetails.fleet_customer_car.model,
                                serviceDetails.fleet_customer_car.year,
                              ]
                                .filter(Boolean)
                                .join(" ") || "-"}
                            </div>
                          </div>
                        )}

                    {/* Service Type Badge */}
                    {serviceDetails?.serviceType && (
                      <div className="mb-4">
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          serviceDetails.serviceType === 'maintenance' 
                            ? 'bg-sky-100 text-sky-700 border border-sky-200' 
                            : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                        }`}>
                          {serviceDetails.serviceType === 'maintenance' ? 'Maintenance' : 'Service'}
                        </span>
                      </div>
                    )}

                    {/* Services List */}
                    {Array.isArray(serviceDetails?.serviceList) && serviceDetails.serviceList.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FiTool size={14} className="text-emerald-600" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Services</span>
                        </div>
                        <ul className="space-y-2">
                          {serviceDetails.serviceList.map((service, index) => (
                            <li
                              key={`${serviceDetails?.id || reminderId}-service-${index}`}
                              className="flex items-center justify-between rounded-lg border border-emerald-100 bg-white px-3 py-2 text-xs"
                            >
                              <div>
                                <span className="font-medium text-slate-700">{service?.name || service?.itemName || `Service ${index + 1}`}</span>
                                {service?.estimatedTime > 0 && (
                                  <span className="ml-2 text-emerald-600">({service.estimatedTime} min)</span>
                                )}
                              </div>
                              <span className="font-semibold text-emerald-700">{formatCurrency(service?.cost ?? service?.price ?? 0)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Service Bundles List */}
                    {Array.isArray(serviceDetails?.bundleList) && serviceDetails.bundleList.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FiTool size={14} className="text-blue-600" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">Service Bundles</span>
                        </div>
                        <ul className="space-y-2">
                          {serviceDetails.bundleList.map((bundle, index) => (
                            <li
                              key={`${serviceDetails?.id || reminderId}-bundle-${index}`}
                              className="rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-700">{bundle?.name || `Bundle ${index + 1}`}</span>
                                <span className="font-semibold text-blue-700">{formatCurrency(bundle?.cost ?? bundle?.price ?? 0)}</span>
                              </div>
                              {/* Tire Details - shown if bundle has tire information */}
                              {(bundle?.tireCost !== undefined && bundle?.tireCost !== null) && (
                                <div className="mt-2 pt-2 border-t border-blue-50">
                                  <div className="flex items-center gap-1 mb-1">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-500">Tire Details</span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                    {bundle?.tireType && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Type:</span>
                                        <span className="font-medium text-slate-700">{bundle.tireType}</span>
                                      </div>
                                    )}
                                    {bundle?.tireQuantity !== undefined && (
                                      <div className="flex justify-between">
                                        <span className="text-slate-500">Quantity:</span>
                                        <span className="font-medium text-slate-700">{bundle.tireQuantity}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Price Per Tire:</span>
                                      <span className="font-medium text-blue-600">{formatCurrency(bundle.tireCost)}</span>
                                    </div>
                                    {bundle?.tireNotes && (
                                      <div className="col-span-2 flex gap-1">
                                        <span className="text-slate-500">Notes:</span>
                                        <span className="font-medium text-slate-700">{bundle.tireNotes}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Parts / Materials List */}
                    {Array.isArray(serviceDetails?.materialList) && serviceDetails.materialList.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FiTool size={14} className="text-amber-600" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">Parts / Materials</span>
                        </div>
                        <ul className="space-y-2">
                          {serviceDetails.materialList.map((material, index) => (
                            <li
                              key={`${serviceDetails?.id || reminderId}-material-${index}`}
                              className="flex items-center justify-between rounded-lg border border-amber-100 bg-white px-3 py-2 text-xs"
                            >
                              <div>
                                <span className="font-medium text-slate-700">{material?.name || `Part ${index + 1}`}</span>
                                {material?.estimatedTime > 0 && (
                                  <span className="ml-2 text-amber-600">({material.estimatedTime} min)</span>
                                )}
                              </div>
                              <span className="font-semibold text-amber-700">{formatCurrency((material?.cost ?? 0) + (material?.labour ?? 0))}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Custom Services List */}
                    {Array.isArray(serviceDetails?.customList) && serviceDetails.customList.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FiTool size={14} className="text-purple-600" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-purple-600">Custom Services</span>
                        </div>
                        <ul className="space-y-2">
                          {serviceDetails.customList.map((custom, index) => (
                            <li
                              key={`${serviceDetails?.id || reminderId}-custom-${index}`}
                              className="flex items-center justify-between rounded-lg border border-dashed border-purple-200 bg-white px-3 py-2 text-xs"
                            >
                              <span className="font-medium text-slate-700">{custom?.name || custom?.itemName || `Custom Service ${index + 1}`}</span>
                              <span className="font-semibold text-purple-700">{formatCurrency(custom?.cost ?? custom?.price ?? 0)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Tires List */}
                    {Array.isArray(serviceDetails?.tireList) && serviceDetails.tireList.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FiTool size={14} className="text-violet-600" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-violet-600">Tires</span>
                        </div>
                        <ul className="space-y-2">
                          {serviceDetails.tireList.map((tire, index) => (
                            <li
                              key={`${serviceDetails?.id || reminderId}-tire-${index}`}
                              className="flex items-center justify-between rounded-lg border border-violet-100 bg-white px-3 py-2 text-xs"
                            >
                              <div>
                                <span className="font-medium text-slate-700">{tire?.name || "Tire"} x {tire?.quantity || 0}</span>
                                {tire?.notes && (
                                  <span className="ml-2 text-violet-600">({tire.notes})</span>
                                )}
                              </div>
                              <span className="font-semibold text-violet-700">{formatCurrency((tire?.cost ?? 0) * (tire?.quantity || 1))}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Cost Breakdown */}
                    {(serviceDetails?.technicianCost > 0 || serviceDetails?.materialCost > 0 || serviceDetails?.tax > 0) && (
                      <div className="mb-4 p-3 rounded-xl bg-white border border-slate-100 space-y-2">
                        {(serviceDetails?.technicianCost > 0 || serviceDetails?.materialCost > 0) && (
                          <>
                            {serviceDetails?.technicianCost > 0 && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-600">Technician Cost</span>
                                <span className="font-medium text-slate-700">{formatCurrency(serviceDetails.technicianCost)}</span>
                              </div>
                            )}
                            {serviceDetails?.materialCost > 0 && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-600">Material Cost</span>
                                <span className="font-medium text-slate-700">{formatCurrency(serviceDetails.materialCost)}</span>
                              </div>
                            )}
                          </>
                        )}
                        {serviceDetails?.tax > 0 && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-600">
                              Tax {serviceDetails?.taxRatePercent ? `(${serviceDetails.taxRatePercent}%)` : ""}
                            </span>
                            <span className="font-medium text-slate-700">{formatCurrency(serviceDetails.tax)}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Total */}
                    {serviceDetails?.totalCost !== undefined && serviceDetails?.totalCost !== null && (
                      <div className="flex items-center justify-between pt-3 border-t border-emerald-200">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-600">Total Amount</span>
                        </div>
                        <span className="text-xl font-bold text-emerald-700">{formatCurrency(serviceDetails.totalCost)}</span>
                      </div>
                    )}

                    {/* Status */}
                    {serviceDetails?.status && (
                      <div className="mt-3 pt-3 border-t border-emerald-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</span>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            serviceDetails.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                            serviceDetails.status === 'Approved' ? 'bg-emerald-100 text-emerald-700' :
                            serviceDetails.status === 'Completed' ? 'bg-blue-100 text-blue-700' :
                            serviceDetails.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {serviceDetails.status}
                          </span>
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {!isLoading && hasReminders && (
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-medium text-emerald-600">Page {currentPage}</div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={goToPreviousPage}
              disabled={!canGoBack}
              className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors ${canGoBack
                ? "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50"
                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                }`}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={goToNextPage}
              disabled={!canGoForward}
              className={`px-5 py-2 rounded-full border text-sm font-semibold transition-colors ${canGoForward
                ? "border-emerald-500 bg-emerald-600 text-white shadow-[0_10px_25px_rgba(16,185,129,0.25)] hover:bg-emerald-700"
                : "border-emerald-100 bg-emerald-50 text-emerald-300 cursor-not-allowed"
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reminders;
